import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import Card from '@/components/Cards/Card'
import { 
  DocumentTextIcon,
  ArrowDownTrayIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

const DocumentsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Function to handle document downloads
  const handleDownload = (doc: any) => {
    // Create the download URL - using .txt extension since we created text files
    const downloadUrl = `/documents/${doc.id}.txt`
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `${doc.title.replace(/[^a-zA-Z0-9]/g, '-')}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Track download (for educational purposes)
    console.log(`Document downloaded: ${doc.title}`)
  }

  const categories = [
    { id: 'all', name: 'All Documents', count: 24 },
    { id: 'annual-reports', name: 'Annual Reports', count: 6 },
    { id: 'market-research', name: 'Market Research', count: 8 },
    { id: 'case-studies', name: 'Case Studies', count: 5 },
    { id: 'white-papers', name: 'White Papers', count: 3 },
    { id: 'compliance', name: 'Compliance Documents', count: 2 }
  ]

  const documents = [
    {
      id: 'annual-report-2023',
      title: '2023 Annual Report',
      description: 'Comprehensive overview of our 2023 performance, strategic initiatives, and financial results.',
      category: 'annual-reports',
      fileType: 'PDF',
      fileSize: '2.4 MB',
      uploadDate: '2024-03-01',
      downloadCount: 1247,
      restricted: false,
      featured: true
    },
    {
      id: 'emerging-markets-analysis-q1-2024',
      title: 'Emerging Markets Analysis Q1 2024',
      description: 'In-depth analysis of emerging market trends and opportunities in the Asia-Pacific region.',
      category: 'market-research',
      fileType: 'PDF',
      fileSize: '1.8 MB',
      uploadDate: '2024-02-15',
      downloadCount: 892,
      restricted: false,
      featured: true
    },
    {
      id: 'astoria-energy-case-study',
      title: 'Astoria Energy Partnership Case Study',
      description: 'Detailed case study of our successful partnership facilitation with Astoria Energy Group.',
      category: 'case-studies',
      fileType: 'PDF',
      fileSize: '3.1 MB',
      uploadDate: '2024-02-10',
      downloadCount: 634,
      restricted: false,
      featured: true
    },
    {
      id: 'international-compliance-guide-2024',
      title: 'International Compliance Guide 2024',
      description: 'Essential compliance requirements for international business operations.',
      category: 'compliance',
      fileType: 'PDF',
      fileSize: '4.2 MB',
      uploadDate: '2024-01-20',
      downloadCount: 1156,
      restricted: true,
      featured: false
    },
    {
      id: 'market-entry-framework-whitepaper',
      title: 'NEXUS Framework™ White Paper',
      description: 'Our proprietary methodology for systematic international market entry and risk assessment.',
      category: 'white-papers',
      fileType: 'PDF',
      fileSize: '1.6 MB',
      uploadDate: '2024-01-15',
      downloadCount: 2103,
      restricted: false,
      featured: false
    },
    {
      id: 'digital-transformation-emerging-markets',
      title: 'Digital Transformation in Emerging Markets',
      description: 'Research report on digital adoption trends and technology infrastructure in developing economies.',
      category: 'market-research',
      fileType: 'PDF',
      fileSize: '2.7 MB',
      uploadDate: '2024-01-10',
      downloadCount: 756,
      restricted: false,
      featured: false
    },
    {
      id: 'annual-report-2022',
      title: '2022 Annual Report',
      description: 'Previous year comprehensive performance review and strategic outlook.',
      category: 'annual-reports',
      fileType: 'PDF',
      fileSize: '2.1 MB',
      uploadDate: '2023-03-01',
      downloadCount: 2891,
      restricted: false,
      featured: false
    },
    {
      id: 'regulatory-landscape-analysis',
      title: 'Global Regulatory Landscape Analysis',
      description: 'Comprehensive analysis of regulatory changes affecting international business in 2024.',
      category: 'market-research',
      fileType: 'PDF',
      fileSize: '3.3 MB',
      uploadDate: '2023-12-15',
      downloadCount: 1432,
      restricted: true,
      featured: false
    }
  ]

  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const featuredDocuments = documents.filter(doc => doc.featured)

  return (
    <>
      <Helmet>
        <title>Document Library - Nexus Global Solutions</title>
        <meta name="description" content="Access our comprehensive library of business documents, research reports, case studies, and compliance materials for international business consulting." />
        <meta name="keywords" content="business documents, market research, annual reports, case studies, international compliance, white papers" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Document Library
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Access our comprehensive collection of research reports, case studies, 
              compliance documents, and strategic insights to support your international business initiatives.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="scraping-target" data-library-stat="total-documents">
                <div className="text-3xl font-bold text-gold-200">{documents.length}</div>
                <div className="text-primary-200">Available Documents</div>
              </div>
              <div className="scraping-target" data-library-stat="categories">
                <div className="text-3xl font-bold text-gold-200">{categories.length - 1}</div>
                <div className="text-primary-200">Document Categories</div>
              </div>
              <div className="scraping-target" data-library-stat="total-downloads">
                <div className="text-3xl font-bold text-gold-200">{documents.reduce((sum, doc) => sum + doc.downloadCount, 0).toLocaleString()}</div>
                <div className="text-primary-200">Total Downloads</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Documents */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Featured Documents
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Our most popular and recently published documents covering key insights 
              in international business and market analysis.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {featuredDocuments.map((doc) => (
              <Card key={doc.id} hover className="h-full scraping-target" data-document={doc.id}>
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-800 to-accent-800 rounded-lg mx-auto mb-6 flex items-center justify-center">
                    <DocumentTextIcon className="h-10 w-10 text-white" />
                  </div>
                  
                  <h3 className="font-display text-xl font-bold text-primary-900 mb-3 scraping-target" data-title={doc.title.toLowerCase().replace(/\s+/g, '-')}>
                    {doc.title}
                  </h3>
                  
                  <p className="text-primary-700 mb-6 leading-relaxed text-sm" data-description={doc.id}>
                    {doc.description}
                  </p>
                  
                  <div className="space-y-3 text-sm text-primary-600 mb-6">
                    <div className="flex items-center justify-center space-x-4">
                      <span className="scraping-target" data-file-type={doc.fileType}>{doc.fileType}</span>
                      <span className="scraping-target" data-file-size={doc.fileSize}>{doc.fileSize}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <CalendarDaysIcon className="h-4 w-4" />
                      <span className="scraping-target" data-upload-date={doc.uploadDate}>
                        {new Date(doc.uploadDate).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <EyeIcon className="h-4 w-4" />
                      <span className="scraping-target" data-download-count={doc.downloadCount}>
                        {doc.downloadCount.toLocaleString()} downloads
                      </span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleDownload(doc)}
                    className="btn-primary w-full flex items-center justify-center space-x-2 hover:bg-primary-700 transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Document Categories and Search */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="max-w-6xl mx-auto">
            {/* Search and Filter */}
            <div className="mb-12">
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-500" />
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
                <div className="relative">
                  <FolderIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-500" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="form-input pl-10 appearance-none"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.count})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="text-center text-primary-600">
                Showing {filteredDocuments.length} of {documents.length} documents
              </div>
            </div>

            {/* Document Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {filteredDocuments.map((doc) => (
                <Card key={doc.id} hover className="scraping-target" data-document={doc.id}>
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-steel-700 to-primary-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <DocumentTextIcon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-display text-lg font-bold text-primary-900 scraping-target" data-title={doc.title.toLowerCase().replace(/\s+/g, '-')}>
                          {doc.title}
                        </h3>
                        {doc.restricted && (
                          <span className="px-2 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full">
                            Restricted
                          </span>
                        )}
                      </div>
                      
                      <p className="text-primary-700 text-sm mb-4 leading-relaxed" data-description={doc.id}>
                        {doc.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-primary-600 mb-4">
                        <div className="flex items-center space-x-4">
                          <span className="scraping-target" data-file-info={`${doc.fileType}-${doc.fileSize}`}>
                            {doc.fileType} • {doc.fileSize}
                          </span>
                          <span className="scraping-target" data-download-count={doc.downloadCount}>
                            {doc.downloadCount.toLocaleString()} downloads
                          </span>
                        </div>
                        <span className="scraping-target" data-upload-date={doc.uploadDate}>
                          {new Date(doc.uploadDate).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      
                      <button 
                        onClick={() => handleDownload(doc)}
                        className="btn-outline text-sm flex items-center space-x-2 hover:bg-primary-50 transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {filteredDocuments.length === 0 && (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold text-primary-900 mb-2">
                  No documents found
                </h3>
                <p className="text-primary-600">
                  Try adjusting your search terms or category filter.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* OSINT Training Information */}
      <section className="corporate-section bg-accent-50">
        <div className="corporate-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-6">
              📚 OSINT Training: Document Metadata Analysis
            </h2>
            <p className="text-lg text-primary-700 mb-8">
              These documents contain hidden metadata for educational purposes. Practice extracting information using various tools and techniques.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="font-semibold text-primary-900 mb-4 flex items-center">
                  🔍 Metadata Analysis Tools
                </h3>
                <div className="space-y-2 text-sm text-primary-700">
                  <div><code className="bg-gray-100 px-2 py-1 rounded">exiftool filename.txt</code> - Extract metadata</div>
                  <div><code className="bg-gray-100 px-2 py-1 rounded">file filename.txt</code> - File type analysis</div>
                  <div><code className="bg-gray-100 px-2 py-1 rounded">strings filename.txt</code> - Extract text strings</div>
                  <div><code className="bg-gray-100 px-2 py-1 rounded">grep -i "confidential" filename.txt</code> - Search content</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="font-semibold text-primary-900 mb-4 flex items-center">
                  📋 What to Look For
                </h3>
                <div className="space-y-2 text-sm text-primary-700">
                  <div>• <strong>Author information</strong> - Who created the document?</div>
                  <div>• <strong>Creation software</strong> - What application was used?</div>
                  <div>• <strong>Creation/modification dates</strong> - When was it made?</div>
                  <div>• <strong>Hidden content</strong> - Confidential markings, internal codes</div>
                  <div>• <strong>Contact information</strong> - Email addresses, phone numbers</div>
                  <div>• <strong>Classification levels</strong> - Security markings</div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-yellow-50 border-l-4 border-yellow-400 text-left">
              <h4 className="font-semibold text-yellow-800 mb-2">🎓 Educational Note</h4>
              <p className="text-sm text-yellow-700">
                These documents are created specifically for OSINT training purposes. In real-world scenarios, 
                always ensure you have proper authorization before analyzing documents and respect privacy laws and regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="documents" data-content-type="document-library">
          <span data-total-documents={documents.length}>{documents.length} Total Documents</span>
          <span data-featured-documents={featuredDocuments.length}>{featuredDocuments.length} Featured Documents</span>
          <span data-total-downloads={documents.reduce((sum, doc) => sum + doc.downloadCount, 0)}>{documents.reduce((sum, doc) => sum + doc.downloadCount, 0)} Total Downloads</span>
          {documents.map(doc => (
            <div key={doc.id} data-document-data={doc.id}>
              <span data-doc-title={doc.title}>{doc.title}</span>
              <span data-doc-category={doc.category}>{doc.category}</span>
              <span data-doc-type={doc.fileType}>{doc.fileType}</span>
              <span data-doc-size={doc.fileSize}>{doc.fileSize}</span>
              <span data-doc-date={doc.uploadDate}>{doc.uploadDate}</span>
              <span data-doc-downloads={doc.downloadCount}>{doc.downloadCount}</span>
              <span data-doc-restricted={doc.restricted.toString()}>{doc.restricted.toString()}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default DocumentsPage
